import axios from "axios";
import ErrorModal from "./components/Modals/ErrorModal/ErrorModal";
import ModalHelper from "./helpers/modalHelper";
import * as React from "react";
import { GetCurrentGameId } from "./constants/Games";

const GENERIC_ERROR = "Something went wrong. Please try again.";

const toDisplayError = (data) => {
	if (typeof data === "string") {
		return { message: data.trim() || GENERIC_ERROR };
	}
	if (!data || typeof data !== "object") {
		return { message: GENERIC_ERROR };
	}
	return {
		message: data.message || data.title || GENERIC_ERROR,
		errorId: data.errorId,
		detail: data.detail,
	};
};

const handleError = (error) => {
	const response = error.response;
	// If the request didnt't complete, response is undefined.
	// Trying to use that response leads to confusing error states.
	if (!response) {
		throw new Error(error.message || "The request did not complete.");
	}
	if (response.status === 401) {
		const unauthorized = new Error("Unauthorized");
		unauthorized.status = 401;
		throw unauthorized;
	}

	const display = toDisplayError(response.data);
	// 403/404 stay silent, several flows rely on that.
	if (response.status === 400 || response.status >= 500) {
		ModalHelper.openModal(
			<ErrorModal
				{...display}
				displayContactDiscord={response.status >= 500}
			/>,
			null
		);
	}

	const failure = new Error(display.message);
	failure.status = response.status;
	failure.errorId = display.errorId;
	throw failure;
};

class AxiosWrapper {
	cache = new Map();

	get = async (url) => {
		const config = await this.getConfig();

		try {
			const result = await axios.get(url, config);
			return result.data;
		} catch (error) {
			handleError(error);
		}
	};

	getCached = async (url) => {
		if (this.cache.has(url)) {
			return Promise.resolve(this.cache.get(url));
		}

		return this.get(url).then((value) => {
			this.cache.set(url, value);

			return value;
		});
	};

	/**
	 * Used to clear a specific url from cache.
	 *
	 * @param {string | null} url
	 */
	clearCache = (url) => {
		if (!url) {
			this.cache = new Map();
			return;
		}

		if (this.cache.has(url)) {
			this.cache.delete(url);
		}
	};

	getConfig = async () => {
		const config = {};
		config.headers = {};

		config.headers.Game = GetCurrentGameId();

		return config;
	};

	post = async (url, data) => {
		const config = await this.getConfig();

		try {
			const result = await axios.post(url, data, config);
			return result.data;
		} catch (error) {
			handleError(error);
		}
	};

	put = async (url, data) => {
		const config = await this.getConfig();

		try {
			const result = await axios.put(url, data, config);
			return result.data;
		} catch (error) {
			handleError(error);
		}
	};

	delete = async (url) => {
		const config = await this.getConfig();

		try {
			const result = await axios.delete(url, config);
			return result.data;
		} catch (error) {
			handleError(error);
		}
	};
}

const axiosWrapper = new AxiosWrapper();
export default axiosWrapper;
